import {
  FieldText,
  FieldTextarea,
  FieldTextareaProps,
  FieldTextProps,
  FlexBox,
} from '@dixa/lib-shared-habla';
import { CSSProperties } from 'react';
import * as React from 'react';

import styles from './charLimitFieldText.module.scss';

interface Props
  extends Omit<FieldTextProps | FieldTextareaProps, 'maxLength' | 'noAlert'> {
  maxLength: number;
  inputType: 'text' | 'textarea';
  wrapperStyle?: CSSProperties;
}

export function CharLimitFieldText({
  maxLength,
  inputType,
  wrapperStyle,
  input,
  ...props
}: Props): JSX.Element {
  if (maxLength < 1) {
    throw new Error(
      `maxLength values expected to be >= 1, actual: ${maxLength}`,
    );
  }

  let Input;
  switch (inputType) {
    case 'text':
      Input = FieldText;
      break;
    case 'textarea':
      Input = FieldTextarea;
      break;
    default:
      Input = null;
      throw new Error('Unknown input type for CharLimitFieldText');
  }

  const handleChange = React.useCallback(
    (
      event: React.ChangeEvent<HTMLTextAreaElement> &
        React.ChangeEvent<HTMLInputElement>,
    ) => {
      if (countStringChars(event.target.value) <= maxLength) {
        input.onChange?.(event);
      }
    },
    [input, maxLength],
  );

  // Unicode-proof way to get string length in codepoints (quick but sub-optimal solution)
  // This can be replaced with the Intl.Segmenter API once it lands in all browsers.
  // example: Array.from(new Intl.Segmenter(locale, {granularity: "grapheme"})).segment(string), data => data.segment)
  const charCount = countStringChars(input.value);
  const charLeft = maxLength - charCount;

  return (
    <div style={wrapperStyle}>
      {Input ? (
        <Input {...props} input={input} onChange={handleChange} noAlert />
      ) : null}
      <FlexBox className={styles.charLimitField}>
        {`${charLeft} characters left`}
      </FlexBox>
    </div>
  );
}

/*
  It turns out that there's no easy way of counting string length when you have emojis in it.
  An emoji can have multiple code-points which results in "👩‍❤️‍💋‍👩".length === 11
  This issue is not solvable without `Intl.Segmenter` API in browsers which is currently not implemented
  in most browsers. The function below tries is probably the best we have for now.
  @see https: //blog.jonnew.com/posts/poo-dot-length-equals-two
 */
function countStringChars(str: string): number {
  const joiner = '\u{200D}';
  const split = str.split(joiner);
  let count = 0;

  for (const s of split) {
    //removing the variation selectors
    const num = Array.from(s.split(/[\ufe00-\ufe0f]/).join('')).length;
    count += num;
  }

  //assuming the joiners are used appropriately
  return count / split.length;
}
